// AI Generated Component
import {
    Component,
    Input,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ElementRef,
} from '@angular/core';
import { PagePerformanceChartsService } from '../page-performance-charts/page-performance-charts.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { MonNumberService } from '@monsido/core/locale/services/mon-number.service';

import { safeClone } from '@client/app/shared/utils/object-utils';
@Component({
    selector: 'page-performance-score-line-chart',
    standalone: false,
    templateUrl: './page-performance-score-line-chart.component.html',
})
export class PagePerformanceScoreLineChartComponent implements OnInit, OnChanges, OnDestroy {
    @Input() data: any;
    @Input() height: any;
    @Input() width: any;
    @Input() milestones: any; // Array<{link: string; text: string; date: string}>

    dataGroups: any[] = [];
    milestonesToDisplay: any[] = [];
    chartSetupDone = false;
    chart: any = {};
    tooltipStyle: any = { display: 'none' };
    headerStyle: any = { display: 'none' };
    tooltip: any = {};
    container: any;
    svg: any;
    onResizeHandler: any;

    constructor (
        private elementRef: ElementRef,
        private pagePerformanceChartsService: PagePerformanceChartsService,
        private dateTimeService: DateTimeService,
        private monNumberService: MonNumberService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnChanges (changes: SimpleChanges): void {
        this.onChanges(changes);
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    trackByIndex (index: number): number {
        return index;
    }

    trackByMilestoneIndex (index: number, milestone: any): number {
        return index;
    }

    activate (): void {
        this.onResizeHandler = this.debounce(() => {
            this.width = this.container.clientWidth;
            this.setupChartData();
        }, 500);

        this.height = this.height || 160;
        this.tooltipStyle = {
            display: 'none',
        };
        this.headerStyle = {
            display: 'none',
        };
        this.tooltip = {};
        this.data = this.data || [];
        this.container = this.elementRef.nativeElement.querySelector('.score-line-container');

        setTimeout(() => {
            this.width = this.width || this.elementRef.nativeElement.querySelector('.score-line-container').clientWidth;
            this.setupChartData();
        });

        window.addEventListener('resize', this.onResizeHandler);
    }

    onChanges (changes: SimpleChanges): void {
        this.width = this.width || this.elementRef.nativeElement.querySelector('.score-line-container').clientWidth;
        this.setupChartData();
    }

    onDestroy (): void {
        window.removeEventListener('resize', this.onResizeHandler);
    }

    setupChartData (): void {
        this.dataGroups = [];
        if (!this.data || this.data.length === 0) {
            return;
        }
        this.chartSetupDone = false;
        this.chart = {
            stepWidth: Math.round(this.width / this.data.length),
            height: this.height * 0.8,
            baseY: 0,
        };
        this.chart.baseX = (this.width - this.chart.stepWidth * (this.data.length - 1)) / 2;
        this.chart.maxScore = 100;
        if (this.data.length === 1) {
            this.data.unshift(this.data[0]);
        }
        for (let i = 0; i < this.data.length - 1; i++) {
            const startScore = this.data[i].score || 0;
            const endScore = this.data[i + 1].score || 0;
            const startTime = this.data[i].time;
            const endTime = this.data[i + 1].time;
            const dataGroup = {
                start: {
                    x: i * this.chart.stepWidth,
                    y: this.toPosY(startScore),
                    score: startScore,
                    time: startTime,
                    isBreakPoint: this.data.length === 2,
                },
                end: {
                    x: (i + 1) * this.chart.stepWidth,
                    y: this.toPosY(endScore),
                    score: endScore,
                    time: endTime,
                },
            };
            const breakPoints = this.isContainBreakPoints(dataGroup.start, dataGroup.end);
            let lastPoint = safeClone(dataGroup.start);
            if (breakPoints.length > 0) {
                for (let j = 0; j < breakPoints.length; j++) {
                    this.dataGroups.push({
                        start: lastPoint,
                        end: breakPoints[j],
                    });
                    lastPoint = safeClone(breakPoints[j]);
                }
                this.dataGroups.push({
                    start: lastPoint,
                    end: dataGroup.end,
                });
            } else {
                this.dataGroups.push(dataGroup);
            }
        }
        const highestPoint = this.dataGroups.reduce((res, cur) => {
            if (res > cur.start.y) {
                res = cur.start.y;
            }
            if (res > cur.end.y) {
                res = cur.end.y;
            }
            return res;
        }, this.height * 0.8);
        this.headerStyle = {
            display: 'block',
            position: 'absolute',
            'padding-left': this.chart.baseX,
            top: highestPoint / 2 - 40,
        };
        this.chart.baseY = -highestPoint / 2 + 5;
        this.chartSetupDone = true;

        setTimeout(() => {
            this.svg = this.elementRef.nativeElement.querySelector('#performance-score-line-chart');
            this.svg?.setAttribute('height', this.height);
            this.svg?.setAttribute('width', this.width);
            if (Array.isArray(this.milestones) && this.milestones.length) {
                this.setMilestones();
            }
        });
    }

    groupLine (dataset: any): string {
        const start = dataset.start;
        const end = dataset.end;
        let path = '';
        const baseX = this.chart.baseX;
        const baseY = this.chart.baseY;
        path += 'M' + (baseX + start.x) + ' ' + (baseY + start.y) + ' ';
        path += 'L' + (baseX + end.x) + ' ' + (baseY + end.y) + ' ';
        return path;
    }

    groupLineFillColor (dataset: any): string {
        return this.pagePerformanceChartsService.getColorByScore(dataset.end.score * 100, 'dark');
    }

    groupPath (dataset: any): string {
        const start = dataset.start;
        const end = dataset.end;
        let path = '';
        const baseX = this.chart.baseX;
        const baseY = this.chart.baseY;
        path += 'M' + (baseX + start.x) + ' ' + (baseY + start.y) + ' ';
        path += 'L' + (baseX + end.x) + ' ' + (baseY + end.y) + ' ';
        path += 'L' + (baseX + end.x) + ' ' + (baseY + this.height) + ' ';
        path += 'L' + (baseX + start.x) + ' ' + (baseY + this.height) + ' ';
        path += 'Z';
        return path;
    }

    groupPathFillColor (dataset: any): string {
        return this.pagePerformanceChartsService.getColorByScore(dataset.end.score * 100, 'light');
    }

    calculateXOnlineFromPoints (start: any, end: any, y: any): number {
        const a = end.y - start.y;
        const b = start.x - end.x;
        const c = a * start.x + b * start.y;
        if (b < 0) {
            return Math.round((c - b * y) / a);
        } else {
            return Math.round((c + b * y) / a);
        }
    }

    toPosY (score: any): number {
        score = score * 100 || 1;
        return this.height - (this.chart.height * score) / this.chart.maxScore - 5;
    }

    isContainBreakPoints (start: any, end: any): any[] {
        const breakPoints = [0.49, 0.89, 0.9];
        const containsBreakPoints: any[] = [];
        for (let i = 0; i < breakPoints.length; i++) {
            const breakPoint = breakPoints[i];
            if (breakPoint >= start.score && breakPoint <= end.score) {
                containsBreakPoints.push({
                    x: this.calculateXOnlineFromPoints(start, end, this.toPosY(breakPoint)),
                    y: this.toPosY(breakPoint),
                    score: breakPoint,
                    isBreakPoint: true,
                });
            }
        }
        return containsBreakPoints;
    }

    setTooltip (event: any, dataset: any): void {
        if (event.target.tagName !== 'path' || !this.svg || !event.originalEvent) {
            return;
        }
        const anchor: any = {};
        const mouseX = event.originalEvent.clientX - this.svg.getBoundingClientRect().left - this.svg.clientLeft - this.chart.baseX;
        if (mouseX > dataset.start.x + (dataset.end.x - dataset.start.x) / 2) {
            anchor.x = dataset.end.x;
            anchor.y = this.chart.baseY + dataset.end.y;
            if (dataset.end.isBreakPoint) {
                return;
            }
            this.tooltip.score = dataset.end.score;
            this.tooltip.time = dataset.end.time;
        } else {
            anchor.x = dataset.start.x;
            anchor.y = this.chart.baseY + dataset.start.y;
            if (dataset.start.isBreakPoint) {
                return;
            }
            this.tooltip.score = dataset.start.score;
            this.tooltip.time = dataset.start.time;
        }

        this.tooltipStyle.left = anchor.x + 18 + 'px';
        this.tooltipStyle.top = anchor.y + 12 + 'px';
        this.tooltipStyle.display = 'block';
        this.tooltipStyle.color = this.pagePerformanceChartsService.getColorByScore(dataset.end.score * 100, 'dark');

        this.tooltip.time = this.dateTimeService.formatBrowserLocalized(this.tooltip.time, 'll LTS');
    }

    disableTooltip (): void {
        this.tooltipStyle.display = 'none';
    }

    getHeaderStyleString (): string {
        return Object.keys(this.headerStyle)
            .map(key => `${key}: ${this.headerStyle[key]}`)
            .join('; ');
    }

    getTooltipStyleString (): string {
        return Object.keys(this.tooltipStyle)
            .map(key => `${key}: ${this.tooltipStyle[key]}`)
            .join('; ');
    }

    getTooltipScoreFormatted (): string {
        return this.monNumberService.transformNumber(this.tooltip.score * 100, '0,0');
    }

    setMilestones (): void {
        if (this.dataGroups.length) {
            this.milestonesToDisplay = this.milestones.map((milestone: any) => {
                const correspondingPoint = this.dataGroups[this.getClosestDateIndex(milestone.date, this.dataGroups)];
                const positionX = correspondingPoint && correspondingPoint.start && correspondingPoint.start.x;
                return {
                    text: milestone.text,
                    link: milestone.link,
                    x: this.chart.baseX + positionX + 1,
                };
            });
            setTimeout(() => this.adjustMilestonesTextPosition());
        }
    }

    getClosestDateIndex (date: any, dates: any): number {
        let result = -1;
        let diff;
        let diffCandidate;
        const len = dates.length;
        let dateToCheck;
        const timestamp = new Date(date).valueOf();
        diff = new Date(date).valueOf();

        for (let i = 0; i < len; i += 1) {
            dateToCheck = (dates[i].start || {}).time;
            if (dateToCheck) {
                diffCandidate = Math.abs(timestamp - new Date(dateToCheck).valueOf());
                if (diffCandidate < diff) {
                    diff = diffCandidate;
                    result = i;
                }
            }
        }

        return result;
    }

    adjustMilestonesTextPosition (): void {
        this.svg = this.elementRef.nativeElement.querySelector('#performance-score-line-chart');
        if (this.svg) {
            Array.prototype.forEach.call(this.svg.querySelectorAll('.milestoneText'), (el: any) => {
                const svgBounds = this.svg.getBoundingClientRect();
                const elBounds = el.getBoundingClientRect();
                const widthShift = Math.round(elBounds.width / 2);
                const originalX = Math.round(Number(el.getAttribute('data-x')));
                const shiftHalfToLeft = originalX - widthShift;
                const eLOriginalLeft = svgBounds.left + originalX;

                if (eLOriginalLeft + elBounds.width - widthShift >= svgBounds.right) {
                    el.setAttribute('x', svgBounds.width - elBounds.width);
                } else if (eLOriginalLeft + shiftHalfToLeft < svgBounds.left) {
                    el.setAttribute('x', 0);
                } else {
                    el.setAttribute('x', shiftHalfToLeft);
                }
            });
        }
    }

    private debounce (func: (...args: any[]) => void, wait: number): (...args: any[]) => void {
        let timeout: any;
        return function executedFunction (...args: any[]) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
}
